import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@codes/ui-kit/components/ui/alert';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Button } from '@codes/ui-kit/components/ui/button';
import { Checkbox } from '@codes/ui-kit/components/ui/checkbox';
import { useToast } from '@codes/ui-kit/components/ui/use-toast';
import { AlertTriangle, Download, Loader2, Lock, Send } from 'lucide-react';

import { CACHE_INVALIDATION_DELAY_MS } from '@codes/codes-keys';

import { CopyButton } from './CopyButton';
import { MESSAGE_TYPE } from '../types';
import { downloadApiKeyFile } from '../utils';

import type { CallbackInfo } from '../types';

interface CodesKeySuccessDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    apiKey: string;
    keyName: string;
    callbackInfo?: CallbackInfo | null;
}

export const CodesKeySuccessDialog = ({
    open,
    onOpenChange,
    apiKey,
    keyName,
    callbackInfo,
}: CodesKeySuccessDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [hasConfirmed, setHasConfirmed] = useState(false);
    const [copied, setCopied] = useState(false);
    const [sent, setSent] = useState(false);
    const [isSending, setIsSending] = useState(false);

    const handleClose = () => {
        setHasConfirmed(false);
        setCopied(false);
        setSent(false);
        setIsSending(false);
        onOpenChange(false);
    };

    const handleDownload = () => {
        downloadApiKeyFile(keyName, apiKey);
    };

    const handleCopySuccess = () => {
        setCopied(true);
    };

    const handleCopyKey = async () => {
        try {
            await navigator.clipboard.writeText(apiKey);
            setCopied(true);
        } catch (error) {
            toast({
                title: t('codesKeys.copyButton.failedTitle'),
                description: t('codesKeys.copyButton.failedDescription'),
                variant: 'destructive',
            });
        }
    };

    const handleSendToFlow = async () => {
        if (!callbackInfo || !window.opener) {
            return;
        }

        setIsSending(true);

        // Wait for server-side data synchronization
        await new Promise(resolve => setTimeout(resolve, CACHE_INVALIDATION_DELAY_MS));

        const message = {
            type: MESSAGE_TYPE,
            payload: {
                apiKey,
                state: callbackInfo.state,
            },
        };

        window.opener.postMessage(message, callbackInfo.origin);
        setSent(true);

        setTimeout(() => {
            window.close();
        }, 500);
    };

    const isCallbackMode = !!callbackInfo && !!window.opener;

    return (
        <AlertDialog open={open} onOpenChange={handleClose}>
            <AlertDialogContent className="sm:max-w-xl">
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                        <Lock className="h-5 w-5 text-green-600" />
                        {t('codesKeys.successDialog.title')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>{t('codesKeys.successDialog.description')}</AlertDialogDescription>
                </AlertDialogHeader>

                <Alert variant="warning" className="my-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{t('codesKeys.successDialog.warningTitle')}</AlertTitle>
                    <AlertDescription>{t('codesKeys.successDialog.warning')}</AlertDescription>
                </Alert>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-muted-foreground">{t('codesKeys.apiKey')}</label>
                        <div className="relative">
                            <code className="block p-4 pr-12 bg-slate-950 text-green-400 rounded-lg font-mono text-sm break-all border border-slate-800">
                                {apiKey}
                            </code>
                            <div className="absolute top-2 right-2">
                                <CopyButton value={apiKey} onCopySuccess={handleCopySuccess} />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        {isCallbackMode ? (
                            <Button
                                onClick={handleSendToFlow}
                                variant="default"
                                className="flex-1"
                                disabled={sent || isSending}
                            >
                                {isSending ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Send className="h-4 w-4 mr-2" />
                                )}
                                {isSending
                                    ? t('codesKeys.flow.sending')
                                    : sent
                                    ? t('codesKeys.flow.sent')
                                    : t('codesKeys.flow.sendToFlow')}
                            </Button>
                        ) : (
                            <Button onClick={handleCopyKey} variant="default" className="flex-1" disabled={copied}>
                                {copied ? t('codesKeys.copied') : t('codesKeys.copyKey')}
                            </Button>
                        )}
                        <Button onClick={handleDownload} variant="outline" className="flex-1">
                            <Download className="h-4 w-4 mr-2" />
                            {t('codesKeys.download')}
                        </Button>
                    </div>

                    {!isCallbackMode && (
                        <div className="flex items-start gap-2 pt-4 border-t">
                            <Checkbox
                                id="confirm-saved"
                                checked={hasConfirmed}
                                onCheckedChange={checked => setHasConfirmed(checked === true)}
                            />
                            <label htmlFor="confirm-saved" className="text-sm leading-tight cursor-pointer">
                                {t('codesKeys.successDialog.confirmSaved')}
                            </label>
                        </div>
                    )}
                </div>

                <AlertDialogFooter>
                    <AlertDialogAction onClick={handleClose} disabled={!isCallbackMode && !hasConfirmed && !copied}>
                        {hasConfirmed || copied || isCallbackMode
                            ? t('codesKeys.successDialog.done')
                            : t('codesKeys.successDialog.close')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
